import { useEffect, useMemo, useRef } from "preact/hooks";
import { AltHintTarget } from "../../components/AltHintTarget/AltHintTarget.jsx";
import { ColumnChart } from "../../components/ColumnChart/ColumnChart.jsx";
import { LineChart } from "../../components/LineChart/LineChart.jsx";
import { Combobox } from "../../components/Combobox/Combobox.jsx";
import { ControlBar } from "../../components/ControlBar/ControlBar.jsx";
import { Panel } from "../../components/Panel/Panel.jsx";
import { formatCurrency } from "../../lib/formatters.js";
import { createSectionNavigationBindings } from "../../lib/shortcuts/createSectionNavigationBindings.js";
import { onKey } from "../../lib/shortcuts/onKey.js";
import { useShortcutBindings } from "../../lib/shortcuts/useShortcutBindings.js";
import { useSessionStorageState } from "../../lib/useSessionStorageState.js";
import {
  ANALYSIS_CHARTS_CATEGORY_SESSION_KEY,
  ANALYSIS_CHARTS_PAYMENT_METHOD_SESSION_KEY,
  ANALYSIS_CHARTS_SERVICE_SESSION_KEY,
  ANALYSIS_CHARTS_SUPPLIER_SESSION_KEY,
  clearAnalysisChartsPendingMetric,
  getAnalysisChartsPendingMetric
} from "../../lib/pageSearchState.js";
import {
  ANALYSIS_CHART_METRIC_ALL,
  ANALYSIS_CHART_METRIC_CATEGORY,
  ANALYSIS_CHART_METRIC_PAYMENT_METHOD,
  ANALYSIS_CHART_METRIC_PROJECTION,
  ANALYSIS_CHART_METRIC_SERVICE,
  ANALYSIS_CHART_METRIC_SUPPLIER
} from "../../lib/settings/profileSettings.js";
import { listExpenseMethodOptions, listExpenseServiceOptions, listExpenseSupplierOptions } from "../expenses/expenseValueOptions.js";
import { buildMonthlyExpenseColumns, buildProjectionSeries } from "./analysisChartsData.js";
import "./AnalysisCharts.css";

const noop = () => {};

function AnalysisStackedMonthChart({
  budgetRows = [],
  expenseRows = [],
  metric = ANALYSIS_CHART_METRIC_ALL,
  selectedCategory = "",
  t = (key) => key
}) {
  const columns = useMemo(() => buildMonthlyExpenseColumns({
    budgetRows,
    expenseRows,
    metric,
    selectedCategory,
    t
  }), [budgetRows, expenseRows, metric, selectedCategory, t]);
  const hasBudgetLine = columns.some((column) => Number(column.markerValue ?? 0) > 0);

  return (
    <div class="analysis-charts-card">
      <div class="analysis-charts-legend">
        <span class="analysis-charts-legend-item">
          <span class="analysis-charts-legend-swatch tone-ongoing" />
          {t("ongoing")}
        </span>
        <span class="analysis-charts-legend-item">
          <span class="analysis-charts-legend-swatch tone-irregular" />
          {t("irregular")}
        </span>
        {hasBudgetLine ? (
          <span class="analysis-charts-legend-item">
            <span class="analysis-charts-legend-line tone-danger dashed" />
            {t("budget")}
          </span>
        ) : null}
      </div>

      <ColumnChart columns={columns} />
    </div>
  );
}

function AnalysisProjectionChart({ rows = [], expenseRows = [], dateContext, t = (key) => key }) {
  const chartData = useMemo(
    () => buildProjectionSeries(rows, expenseRows, dateContext, t),
    [dateContext, expenseRows, rows, t]
  );

  return (
    <div class="analysis-charts-card">
      <div class="analysis-charts-legend">
        {chartData.series.map((entry) => (
          <span key={entry.key} class="analysis-charts-legend-item">
            <span class={`analysis-charts-legend-line tone-${entry.tone ?? "accent"} ${entry.dashed ? "dashed" : ""}`} />
            {entry.label}
          </span>
        ))}
      </div>

      <LineChart
        labels={chartData.labels}
        series={chartData.series}
        valueFormatter={formatCurrency}
      />
    </div>
  );
}

export function AnalysisCharts({
  rows = [],
  expenseRows = [],
  dateContext,
  metric = ANALYSIS_CHART_METRIC_ALL,
  onMetricChange,
  showAltHints = false,
  t = (key) => key
}) {
  const metricSelectRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useSessionStorageState(ANALYSIS_CHARTS_CATEGORY_SESSION_KEY, "");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useSessionStorageState(ANALYSIS_CHARTS_PAYMENT_METHOD_SESSION_KEY, "");
  const [selectedService, setSelectedService] = useSessionStorageState(ANALYSIS_CHARTS_SERVICE_SESSION_KEY, "");
  const [selectedSupplier, setSelectedSupplier] = useSessionStorageState(ANALYSIS_CHARTS_SUPPLIER_SESSION_KEY, "");
  const metricValue = [
    ANALYSIS_CHART_METRIC_ALL,
    ANALYSIS_CHART_METRIC_CATEGORY,
    ANALYSIS_CHART_METRIC_PAYMENT_METHOD,
    ANALYSIS_CHART_METRIC_SUPPLIER,
    ANALYSIS_CHART_METRIC_SERVICE,
    ANALYSIS_CHART_METRIC_PROJECTION
  ].includes(metric)
    ? metric
    : ANALYSIS_CHART_METRIC_ALL;
  const onMetricValueChange = onMetricChange ?? noop;
  const metricOptions = useMemo(() => [
    { value: ANALYSIS_CHART_METRIC_ALL, label: t("expenses") },
    { value: ANALYSIS_CHART_METRIC_CATEGORY, label: t("categories") },
    { value: ANALYSIS_CHART_METRIC_SUPPLIER, label: t("suppliers") },
    { value: ANALYSIS_CHART_METRIC_SERVICE, label: t("service") },
    { value: ANALYSIS_CHART_METRIC_PAYMENT_METHOD, label: t("paymentMethod") },
    { value: ANALYSIS_CHART_METRIC_PROJECTION, label: t("projection") }
  ], [t]);
  const categoryOptions = useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(
        expenseRows
          .map((row) => String(row.category ?? "").trim())
          .filter(Boolean)
      )
    );

    return uniqueCategories.sort((left, right) => left.localeCompare(right));
  }, [expenseRows]);
  const serviceOptions = useMemo(() => listExpenseServiceOptions(expenseRows), [expenseRows]);
  const paymentMethodOptions = useMemo(() => listExpenseMethodOptions(expenseRows), [expenseRows]);
  const supplierOptions = useMemo(() => listExpenseSupplierOptions(expenseRows), [expenseRows]);
  const visibleExpenseRows = useMemo(() => {
    if (metricValue === ANALYSIS_CHART_METRIC_CATEGORY) {
      if (!selectedCategory) {
        return [];
      }

      return expenseRows.filter((row) => String(row.category ?? "").trim() === selectedCategory);
    }

    if (metricValue === ANALYSIS_CHART_METRIC_SERVICE) {
      if (!selectedService) {
        return [];
      }

      return expenseRows.filter((row) => String(row.service ?? "").trim() === selectedService);
    }

    if (metricValue === ANALYSIS_CHART_METRIC_PAYMENT_METHOD) {
      if (!selectedPaymentMethod) {
        return [];
      }

      return expenseRows.filter((row) => String(row.method ?? "").trim() === selectedPaymentMethod);
    }

    if (metricValue === ANALYSIS_CHART_METRIC_SUPPLIER) {
      if (!selectedSupplier) {
        return [];
      }

      return expenseRows.filter((row) => String(row.supplier ?? "").trim() === selectedSupplier);
    }

    return expenseRows;
  }, [expenseRows, metricValue, selectedCategory, selectedPaymentMethod, selectedService, selectedSupplier]);
  const visibleTotal = useMemo(
    () => visibleExpenseRows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0),
    [visibleExpenseRows]
  );

  useEffect(() => {
    const pendingMetric = getAnalysisChartsPendingMetric();
    if (!pendingMetric || pendingMetric === metricValue) {
      if (pendingMetric) {
        clearAnalysisChartsPendingMetric();
      }
      return;
    }

    onMetricValueChange(pendingMetric);
    clearAnalysisChartsPendingMetric();
  }, [metricValue, onMetricValueChange]);

  useEffect(() => {
    if (categoryOptions.length === 0) {
      if (selectedCategory !== "") {
        setSelectedCategory("");
      }
      return;
    }

    if (!categoryOptions.includes(selectedCategory)) {
      setSelectedCategory(categoryOptions[0]);
    }
  }, [categoryOptions, selectedCategory, setSelectedCategory]);

  useEffect(() => {
    if (serviceOptions.length === 0) {
      if (selectedService !== "") {
        setSelectedService("");
      }
      return;
    }

    if (!serviceOptions.includes(selectedService)) {
      setSelectedService(serviceOptions[0]);
    }
  }, [selectedService, serviceOptions, setSelectedService]);

  useEffect(() => {
    if (paymentMethodOptions.length === 0) {
      if (selectedPaymentMethod !== "") {
        setSelectedPaymentMethod("");
      }
      return;
    }

    if (!paymentMethodOptions.includes(selectedPaymentMethod)) {
      setSelectedPaymentMethod(paymentMethodOptions[0]);
    }
  }, [paymentMethodOptions, selectedPaymentMethod, setSelectedPaymentMethod]);

  useEffect(() => {
    if (supplierOptions.length === 0) {
      if (selectedSupplier !== "") {
        setSelectedSupplier("");
      }
      return;
    }

    if (!supplierOptions.includes(selectedSupplier)) {
      setSelectedSupplier(supplierOptions[0]);
    }
  }, [selectedSupplier, setSelectedSupplier, supplierOptions]);

  const pageBindings = useMemo(() => [
    ...createSectionNavigationBindings({
      options: metricOptions,
      value: metricValue,
      onChange: onMetricValueChange
    }),
    onKey("Alt+E", () => {
      metricSelectRef.current?.focus();
    }, { allowInInput: true })
  ], [metricOptions, metricValue, onMetricValueChange]);

  useShortcutBindings(window, pageBindings);

  return (
    <Panel
      className="analysis-charts-panel fill-area"
      toolbar={(
        <ControlBar
          boxed={false}
          start={(
            <>
              <AltHintTarget label="E" visible={showAltHints} className="control-bar-item">
                <label class="control-bar-item">
                  <select
                    class="control-bar-select"
                    ref={metricSelectRef}
                    value={metricValue}
                    onChange={(event) => onMetricValueChange(event.currentTarget.value)}
                  >
                    {metricOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </AltHintTarget>

              {metricValue === ANALYSIS_CHART_METRIC_CATEGORY ? (
                <label class="control-bar-item control-bar-item--field">
                  <Combobox
                    options={categoryOptions}
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    maxVisibleOptions={6}
                  />
                </label>
              ) : null}

              {metricValue === ANALYSIS_CHART_METRIC_SERVICE ? (
                <label class="control-bar-item control-bar-item--field">
                  <Combobox
                    options={serviceOptions}
                    value={selectedService}
                    onChange={setSelectedService}
                    maxVisibleOptions={6}
                  />
                </label>
              ) : null}

              {metricValue === ANALYSIS_CHART_METRIC_PAYMENT_METHOD ? (
                <label class="control-bar-item control-bar-item--field">
                  <Combobox
                    options={paymentMethodOptions}
                    value={selectedPaymentMethod}
                    onChange={setSelectedPaymentMethod}
                    maxVisibleOptions={6}
                  />
                </label>
              ) : null}

              {metricValue === ANALYSIS_CHART_METRIC_SUPPLIER ? (
                <label class="control-bar-item control-bar-item--field">
                  <Combobox
                    options={supplierOptions}
                    value={selectedSupplier}
                    onChange={setSelectedSupplier}
                    maxVisibleOptions={6}
                  />
                </label>
              ) : null}
            </>
          )}
          end={(
            <div class="control-bar-summary">
              {formatCurrency(visibleTotal)}
            </div>
          )}
        />
      )}
    >
      <div class="analysis-charts-body">
        {metricValue === ANALYSIS_CHART_METRIC_PROJECTION ? (
          <AnalysisProjectionChart
            rows={rows}
            expenseRows={expenseRows}
            dateContext={dateContext}
            t={t}
          />
        ) : (
          <AnalysisStackedMonthChart
            budgetRows={rows}
            expenseRows={visibleExpenseRows}
            metric={metricValue}
            selectedCategory={selectedCategory}
            t={t}
          />
        )}
      </div>
    </Panel>
  );
}
